import apiService from "./apiService";
import { getMarketTestSnapshot } from "../components/company-overview/marketTestData";
import type {
  MarketCandle,
  MarketFundamentals,
  MarketNewsItem,
  MarketQuote,
  MarketSnapshot,
  MarketSnapshotResult,
} from "../components/company-overview/types";

export type GetMarketSnapshotInput = {
  ticker?: string | null;
  exchange?: string | null;
  companyName?: string | null;
  customerId?: string | null;
  refresh?: boolean;
  /** Fall back to local test snapshot if live fetch fails. */
  useTestData?: boolean;
};

/** "VMARCIND.NS" → "VMARCIND" */
export function normalizeMarketTicker(ticker?: string | null): string {
  return (ticker || "")
    .trim()
    .toUpperCase()
    .replace(/\.(NS|BO|BSE|NSE)$/i, "");
}

/** "NSE" / "NSI" → "nse" */
export function normalizeMarketExchange(
  exchange?: string | null
): string | null {
  if (exchange == null || String(exchange).trim() === "") return null;
  const e = String(exchange).trim().toLowerCase();
  if (e === "bo" || e === "bombay" || e.includes("bse")) return "bse";
  if (e === "nsi" || e === "national" || e.includes("nse")) return "nse";
  return e;
}

const num = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const str = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

function mapQuote(raw: unknown): MarketQuote | null {
  if (!raw || typeof raw !== "object") return null;
  const q = raw as Record<string, unknown>;
  const symbol = str(q.symbol);
  if (!symbol) return null;
  return {
    symbol,
    shortName: str(q.shortName) ?? str(q.longName),
    exchange: str(q.exchange) ?? str(q.fullExchangeName),
    currency: str(q.currency) ?? "INR",
    price: num(q.price) ?? num(q.regularMarketPrice),
    change: num(q.change) ?? num(q.regularMarketChange),
    changePercent:
      num(q.changePercent) ?? num(q.regularMarketChangePercent),
    open: num(q.open) ?? num(q.regularMarketOpen),
    previousClose:
      num(q.previousClose) ??
      num(q.chartPreviousClose) ??
      num(q.regularMarketPreviousClose),
    dayLow: num(q.dayLow) ?? num(q.regularMarketDayLow),
    dayHigh: num(q.dayHigh) ?? num(q.regularMarketDayHigh),
    volume: num(q.volume) ?? num(q.regularMarketVolume),
    marketCap: num(q.marketCap),
    fiftyTwoWeekHigh: num(q.fiftyTwoWeekHigh),
    fiftyTwoWeekLow: num(q.fiftyTwoWeekLow),
    marketTime:
      str(q.marketTime) ??
      (typeof q.regularMarketTime === "number"
        ? new Date(q.regularMarketTime * 1000).toISOString()
        : null),
  };
}

function mapFundamentals(raw: unknown): MarketFundamentals | null {
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  return {
    asOf: str(f.asOf),
    marketCap: num(f.marketCap),
    enterpriseValue: num(f.enterpriseValue),
    trailingPE: num(f.trailingPE) ?? num(f.trailingPe),
    forwardPE: num(f.forwardPE) ?? num(f.forwardPe),
    pegRatio: num(f.pegRatio),
    priceToSales: num(f.priceToSales) ?? num(f.priceToSalesTrailing12Months),
    priceToBook: num(f.priceToBook),
    evToRevenue: num(f.evToRevenue),
    evToEbitda: num(f.evToEbitda) ?? num(f.enterpriseToEbitda),
    profitMargin: num(f.profitMargin) ?? num(f.profitMargins),
    returnOnAssets: num(f.returnOnAssets),
    returnOnEquity: num(f.returnOnEquity),
    revenue: num(f.revenue) ?? num(f.totalRevenue),
    netIncome: num(f.netIncome),
    dilutedEps: num(f.dilutedEps) ?? num(f.trailingEps),
    totalCash: num(f.totalCash),
    debtToEquity: num(f.debtToEquity),
    leveredFreeCashFlow: num(f.leveredFreeCashFlow) ?? num(f.freeCashflow),
  };
}

function mapHistory(raw: unknown): MarketCandle[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const c = row as Record<string, unknown>;
      const date = str(c.date);
      const close = num(c.close);
      if (!date || close === null) return null;
      return {
        date,
        open: num(c.open),
        close,
      } satisfies MarketCandle;
    })
    .filter((c): c is MarketCandle => c !== null);
}

function mapNews(raw: unknown): MarketNewsItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const n = row as Record<string, unknown>;
      const title = str(n.title);
      const url = str(n.url) ?? str(n.link);
      if (!title || !url) return null;
      return {
        title,
        url,
        publisher: str(n.publisher) ?? str(n.source),
        publishedAt:
          str(n.publishedAt) ??
          str(n.pubDate) ??
          (typeof n.providerPublishTime === "number"
            ? new Date(n.providerPublishTime * 1000).toISOString()
            : null),
      } satisfies MarketNewsItem;
    })
    .filter((n): n is MarketNewsItem => n !== null);
}

/** Yahoo Finance chart payload → MarketSnapshot (backend may forward this as-is). */
function mapYahooChart(raw: unknown): MarketSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const root = raw as Record<string, unknown>;
  const chart =
    root.chart && typeof root.chart === "object"
      ? (root.chart as Record<string, unknown>)
      : root;
  const results = chart.result;
  if (!Array.isArray(results) || !results[0] || typeof results[0] !== "object") {
    return null;
  }

  const result = results[0] as Record<string, unknown>;
  const meta =
    result.meta && typeof result.meta === "object"
      ? (result.meta as Record<string, unknown>)
      : null;
  if (!meta) return null;

  const timestamps = Array.isArray(result.timestamp) ? result.timestamp : [];
  const indicators =
    result.indicators && typeof result.indicators === "object"
      ? (result.indicators as Record<string, unknown>)
      : {};
  const quoteSeries =
    Array.isArray(indicators.quote) &&
    indicators.quote[0] &&
    typeof indicators.quote[0] === "object"
      ? (indicators.quote[0] as Record<string, unknown>)
      : {};
  const closes = Array.isArray(quoteSeries.close) ? quoteSeries.close : [];
  const opens = Array.isArray(quoteSeries.open) ? quoteSeries.open : [];

  const history: MarketCandle[] = timestamps
    .map((ts, index) => {
      const close = num(closes[index]);
      if (typeof ts !== "number" || close === null) return null;
      return {
        date: new Date(ts * 1000).toISOString().slice(0, 10),
        open: num(opens[index]),
        close,
      } satisfies MarketCandle;
    })
    .filter((c): c is MarketCandle => c !== null);

  const price =
    num(meta.regularMarketPrice) ??
    num(meta.fulldayPrice) ??
    history[history.length - 1]?.close ??
    null;
  const previousClose =
    num(meta.chartPreviousClose) ?? num(meta.previousClose);
  const change =
    num(meta.fulldayChange) ??
    (price !== null && previousClose !== null ? price - previousClose : null);
  const changePercent =
    num(meta.regularMarketChangePercent) ??
    num(meta.fulldayChangePercent) ??
    (price !== null && previousClose
      ? ((price - previousClose) / previousClose) * 100
      : null);
  const lastOpen =
    num(opens[opens.length - 1]) ?? history[history.length - 1]?.open ?? null;

  const quote: MarketQuote = {
    symbol: str(meta.symbol) ?? "",
    shortName: str(meta.longName) ?? str(meta.shortName),
    exchange: str(meta.fullExchangeName) ?? str(meta.exchangeName),
    currency: str(meta.currency) ?? "INR",
    price,
    change,
    changePercent,
    open: lastOpen,
    previousClose,
    dayLow: num(meta.regularMarketDayLow),
    dayHigh: num(meta.regularMarketDayHigh),
    volume: num(meta.regularMarketVolume),
    marketCap: num(meta.marketCap),
    fiftyTwoWeekHigh: num(meta.fiftyTwoWeekHigh),
    fiftyTwoWeekLow: num(meta.fiftyTwoWeekLow),
    marketTime:
      typeof meta.regularMarketTime === "number"
        ? new Date(meta.regularMarketTime * 1000).toISOString()
        : null,
  };

  if (!quote.symbol && !history.length) return null;

  return {
    quote: quote.symbol ? quote : null,
    fundamentals: mapFundamentals(root.fundamentals),
    history,
    news: mapNews(root.news),
    fetchedAt: str(root.fetchedAt) ?? new Date().toISOString(),
  };
}

function mapSnapshot(raw: unknown): MarketSnapshot | null {
  if (!raw || typeof raw !== "object") return null;

  // Raw Yahoo chart (or { chart: { result: [...] } })
  const fromYahoo = mapYahooChart(raw);
  if (fromYahoo?.history.length || fromYahoo?.quote) return fromYahoo;

  const s = raw as Record<string, unknown>;
  const quote = mapQuote(s.quote);
  // Backend may send fundamentals as `stats` (news + stats payload).
  const fundamentals =
    mapFundamentals(s.fundamentals) ?? mapFundamentals(s.stats);
  const history = mapHistory(s.history);
  const news = mapNews(s.news);
  if (!quote && !fundamentals && !history.length && !news.length) return null;
  return {
    quote,
    fundamentals,
    history,
    news,
    fetchedAt: str(s.fetchedAt) ?? new Date().toISOString(),
  };
}

function resolveMetaSymbol(payload: Record<string, unknown>): string | null {
  const meta =
    payload.metaResolved && typeof payload.metaResolved === "object"
      ? (payload.metaResolved as Record<string, unknown>)
      : null;
  if (!meta) return null;
  return (
    normalizeMarketTicker(str(meta.ticker)) ||
    str(meta.ticker) ||
    null
  );
}

/** Normalize `/customers/getMarketSnapshot` payload into UI types. */
function normalizeApiResult(payload: unknown): MarketSnapshotResult | null {
  if (!payload || typeof payload !== "object") return null;
  const data = payload as Record<string, unknown>;
  const body =
    data.data && typeof data.data === "object"
      ? (data.data as Record<string, unknown>)
      : data;
  const metaSymbol = resolveMetaSymbol(data) ?? resolveMetaSymbol(body);

  if (body.status === "unavailable" || data.success === false) {
    return {
      status: "unavailable",
      message:
        str(body.message) ??
        str(data.message) ??
        "Market data could not be loaded right now.",
    };
  }

  // { status: "ready", symbol, snapshot, cached }
  if (body.snapshot && typeof body.snapshot === "object") {
    const snapshot = mapSnapshot(body.snapshot);
    if (!snapshot) return null;
    return {
      status: "ready",
      symbol:
        str(body.symbol) ??
        metaSymbol ??
        snapshot.quote?.symbol ??
        "",
      snapshot,
      cached: Boolean(body.cached),
    };
  }

  // Flat { news, stats, fetchedAt } or Yahoo chart / { quote, history, ... }
  const snapshot = mapSnapshot(body);
  if (snapshot) {
    // When API returns stats/news without a quote, seed a minimal quote from meta.
    if (!snapshot.quote && metaSymbol) {
      snapshot.quote = {
        symbol: metaSymbol,
        shortName: null,
        exchange: null,
        currency: "INR",
        price: null,
        change: null,
        changePercent: null,
        open: null,
        previousClose: null,
        dayLow: null,
        dayHigh: null,
        volume: null,
        marketCap: snapshot.fundamentals?.marketCap ?? null,
        fiftyTwoWeekHigh: null,
        fiftyTwoWeekLow: null,
        marketTime: snapshot.fetchedAt ?? null,
      };
    }
    return {
      status: "ready",
      symbol: metaSymbol ?? snapshot.quote?.symbol ?? "",
      snapshot,
      cached: Boolean(body.cached),
    };
  }

  return null;
}

function fallbackResult(
  input: GetMarketSnapshotInput,
  message?: string
): MarketSnapshotResult {
  if (input.useTestData) {
    return getMarketTestSnapshot(
      normalizeMarketTicker(input.ticker) || "TEST",
      normalizeMarketExchange(input.exchange)
    );
  }
  return {
    status: "unavailable",
    message: message || "Market data could not be loaded right now.",
  };
}

/**
 * Load market snapshot from Portal backend only.
 * Backend calls Yahoo (`query2.finance.yahoo.com`); no browser Yahoo/Google fetches.
 */
export async function getMarketSnapshot(
  input: GetMarketSnapshotInput
): Promise<MarketSnapshotResult> {
  try {
    const customerId =
      input.customerId != null && String(input.customerId).trim() !== ""
        ? String(input.customerId).trim()
        : null;
    const companyName =
      input.companyName != null && String(input.companyName).trim() !== ""
        ? String(input.companyName).trim()
        : null;
    // Only attach ticker/exchange when explicitly provided for this request.
    const ticker = normalizeMarketTicker(input.ticker) || undefined;
    const exchange = normalizeMarketExchange(input.exchange) || undefined;

    const response = await apiService.post<any>(
      "/customers/getMarketSnapshot",
      {
        customerId,
        companyName,
        ...(ticker ? { ticker } : {}),
        ...(exchange ? { exchange } : {}),
        refresh: input.refresh ? true : undefined,
      }
    );
    const fromApi = normalizeApiResult(response?.data ?? response);
    if (fromApi?.status === "ready") {
      return fromApi;
    }
    if (fromApi?.status === "unavailable") {
      return fallbackResult(input, fromApi.message);
    }
    return fallbackResult(
      input,
      "Market data could not be loaded right now."
    );
  } catch (error) {
    console.error("getMarketSnapshot failed", error);
    return fallbackResult(
      input,
      error instanceof Error
        ? error.message
        : "Market data could not be loaded right now."
    );
  }
}
